import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  FaUsers, FaUserPlus, FaEdit, FaTrash, FaEye,
  FaSearch, FaFilter, FaTimes, FaCheck, FaBan,
  FaShieldAlt, FaUserCog, FaUserCheck, FaSpinner
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ViewAsButton from '../components/ViewAsButton';
import InviteLinkModal from '../components/InviteLinkModal';
import api from '../api';  // ✅ Import default export
import { siteInspectors } from '../api';

function UsersPage({ onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    phone: '',
    permissions: []
  });

  // ✅ PHASE 3.5: Tab + Site Inspectors state
  const [activeTab, setActiveTab] = useState('users');
  const [inspectors, setInspectors] = useState([]);
  const [inspectorsLoading, setInspectorsLoading] = useState(false);
  const [showInspectorForm, setShowInspectorForm] = useState(false);
  const [editingInspector, setEditingInspector] = useState(null);
  const [inspectorForm, setInspectorForm] = useState({
    name: '',
    telephone: '',
    email: '',
    coverage_area: ''
  });

  // ✅ Inspector delete — custom in-app confirm modal (never window.confirm)
  const [showInspectorDeleteModal, setShowInspectorDeleteModal] = useState(false);
  const [inspectorToDelete, setInspectorToDelete] = useState(null);
  const [deletingInspector, setDeletingInspector] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // 🔑 Sign-in link shown after a create when the invite email could not be sent
  const [inviteInfo, setInviteInfo] = useState(null);
  
  // ✅ Get users API from the imported api object
  const usersApi = api.users;

  // ✅ All available permissions (keys match backend checkPermission naming)
  const allPermissions = [
    { key: 'view_clients', label: 'View Clients' },
    { key: 'edit_clients', label: 'Edit Clients' },
    { key: 'delete_clients', label: 'Delete Clients' },
    { key: 'view_subbies', label: 'View Subcontractors' },
    { key: 'edit_subbies', label: 'Edit Subcontractors' },
    { key: 'delete_subbies', label: 'Delete Subcontractors' },
    { key: 'view_jobs', label: 'View Jobs' },
    { key: 'edit_jobs', label: 'Edit Jobs' },
    { key: 'delete_jobs', label: 'Delete Jobs' },
    { key: 'view_quotes', label: 'View Quotes' },
    { key: 'create_quotes', label: 'Create Quotes' },
    { key: 'view_invoices', label: 'View Invoices' },
    { key: 'create_invoices', label: 'Create Invoices' },
    { key: 'send_invoices', label: 'Send Invoices' },
    { key: 'view_reports', label: 'View Reports' },
    { key: 'generate_reports', label: 'Generate Reports' },
    { key: 'view_tenders', label: 'View Tenders' },
    { key: 'create_tenders', label: 'Create Tenders' },
    { key: 'view_settings', label: 'View Settings' },
    { key: 'edit_settings', label: 'Edit Settings' },
    { key: 'manage_users', label: 'Manage Users' },
    { key: 'manage_permissions', label: 'Manage Permissions' },
    { key: 'view_audit_log', label: 'View Audit Log' },
    { key: 'approve_clients', label: 'Approve Clients' },
    { key: 'approve_subbies', label: 'Approve Subcontractors' },
    { key: 'approve_payments', label: 'Approve Payments' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  // ✅ Use usersApi to fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await usersApi.getAll();
      if (data.success) {
        setUsers(data.users || []);
      } else {
        toast.error(data.error || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // ✅ PHASE 3.5: Site Inspectors CRUD
  const fetchInspectors = async () => {
    setInspectorsLoading(true);
    try {
      const data = await siteInspectors.getAll();
      if (data.success) {
        setInspectors(data.inspectors || []);
      } else {
        toast.error(data.error || 'Failed to load site inspectors');
      }
    } catch (error) {
      console.error('Error fetching site inspectors:', error);
      toast.error('Failed to load site inspectors');
    } finally {
      setInspectorsLoading(false);
    }
  };

  const openAddInspector = () => {
    setEditingInspector(null);
    setInspectorForm({ name: '', telephone: '', email: '', coverage_area: '' });
    setShowInspectorForm(true);
  };

  const openEditInspector = (inspector) => {
    setEditingInspector(inspector);
    setInspectorForm({
      name: inspector.name || '',
      telephone: inspector.telephone || '',
      email: inspector.email || '',
      coverage_area: inspector.coverage_area || ''
    });
    setShowInspectorForm(true);
  };

  const handleInspectorFormChange = (field, value) => {
    setInspectorForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveInspector = async (e) => {
    e.preventDefault();
    if (!inspectorForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingInspector) {
        const data = await siteInspectors.update(editingInspector.id, inspectorForm);
        if (data.success) {
          toast.success('Inspector updated successfully');
        } else {
          toast.error(data.error || 'Failed to update inspector');
        }
      } else {
        const data = await siteInspectors.create(inspectorForm);
        if (data.success) {
          toast.success('Inspector added successfully');
        } else {
          toast.error(data.error || 'Failed to add inspector');
        }
      }
      setShowInspectorForm(false);
      fetchInspectors();
    } catch (error) {
      console.error('Error saving inspector:', error);
      toast.error('Failed to save inspector');
    }
  };

  const handleToggleInspectorActive = async (inspector) => {
    try {
      const data = await siteInspectors.update(inspector.id, { active: !inspector.active });
      if (data.success) {
        toast.success(inspector.active ? 'Inspector deactivated' : 'Inspector activated');
        fetchInspectors();
      } else {
        toast.error(data.error || 'Failed to update inspector');
      }
    } catch (error) {
      console.error('Error toggling inspector:', error);
      toast.error('Failed to update inspector');
    }
  };

  const handleDeleteInspector = (inspector) => {
    setInspectorToDelete(inspector);
    setShowInspectorDeleteModal(true);
  };

  const confirmDeleteInspector = async () => {
    if (!inspectorToDelete) return;
    setDeletingInspector(true);
    try {
      const data = await siteInspectors.remove(inspectorToDelete.id);
      if (data.success) {
        toast.success('Inspector deleted');
        setShowInspectorDeleteModal(false);
        setInspectorToDelete(null);
        fetchInspectors();
      } else {
        toast.error(data.error || 'Failed to delete inspector');
      }
    } catch (error) {
      console.error('Error deleting inspector:', error);
      toast.error('Failed to delete inspector');
    } finally {
      setDeletingInspector(false);
    }
  };

  const getPermissionsForRole = (role) => {
    if (role === 'super_admin') {
      return allPermissions.map(p => p.key);
    } else if (role === 'user') {
      // Read-only: only view permissions, no edit/delete/create/manage
      return allPermissions.filter(p => p.key.startsWith('view_')).map(p => p.key);
    } else if (role === 'admin') {
      return formData.permissions || [];
    }
    return [];
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      role: role,
      permissions: role === 'super_admin' 
        ? allPermissions.map(p => p.key) 
        : role === 'user' 
          ? [] 
          : prev.permissions || []
    }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.email || !formData.full_name || !formData.role) {
        toast.error('Email, Full Name, and Role are required');
        setSubmitting(false);
        return;
      }

      const data = await usersApi.create({
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        phone: formData.phone || null,
        permissions: getPermissionsForRole(formData.role)
      });

      if (data.success) {
        const roleLabel = formData.role === 'super_admin' ? 'Super Admin' : formData.role;
        // Capture the values before the reset below clears them.
        const createdEmail = data.email || formData.email;
        const createdName = formData.full_name;
        toast.success(
          data.invite_email_sent
            ? `✅ ${roleLabel} created! Invite email sent.`
            : `✅ ${roleLabel} created — copy the sign-in link below and send it to them.`
        );
        setShowAddModal(false);
        setFormData({ email: '', full_name: '', password: '', role: 'user', phone: '', permissions: [] });
        fetchUsers();
        // 🔑 The account exists, but the email may not have gone out (the
        // SendGrid trial expired) — always offer the link to send by hand.
        if (data.invite_link) {
          setInviteInfo({
            company: createdName,
            email: createdEmail,
            link: data.invite_link,
            emailSent: !!data.invite_email_sent
          });
        } else if (!data.invite_email_sent) {
          toast.warning('User created, but no sign-in link was returned.');
        }
      } else {
        toast.error(data.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!isSuperAdmin) {
        toast.error('Only Super Admin can edit users');
        setSubmitting(false);
        return;
      }

      const data = await usersApi.update(selectedUser.id, {
        full_name: formData.full_name,
        phone: formData.phone,
        role: formData.role,
        permissions: getPermissionsForRole(formData.role),
        is_active: formData.is_active !== undefined ? formData.is_active : true
      });

      if (data.success) {
        toast.success('✅ User updated successfully!');
        setShowEditModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        toast.error(data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (u) => {
    setDeleteTarget(u);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    
    try {
      const data = await usersApi.delete(deleteTarget.id);
      if (data.success) {
        toast.success('✅ User deleted successfully!');
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchUsers();
      } else {
        toast.error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (user) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can edit users');
      return;
    }
    setSelectedUser(user);
    setFormData({
      full_name: user.full_name || '',
      phone: user.phone || '',
      role: user.role || 'user',
      permissions: user.permissions || [],
      is_active: user.is_active !== undefined ? user.is_active : true
    });
    setShowEditModal(true);
  };

  const togglePermission = (permissionKey) => {
    setFormData(prev => {
      const perms = prev.permissions || [];
      if (perms.includes(permissionKey)) {
        return { ...prev, permissions: perms.filter(p => p !== permissionKey) };
      } else {
        return { ...prev, permissions: [...perms, permissionKey] };
      }
    });
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const styles = {
      'super_admin': 'bg-red-100 text-red-600',
      'admin': 'bg-purple-100 text-purple-600',
      'client': 'bg-blue-100 text-blue-600',
      'subcontractor': 'bg-green-100 text-green-600',
      'user': 'bg-gray-100 text-gray-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[role] || styles.user}`;
  };

  const getRoleLabel = (role) => {
    const labels = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'client': 'Client',
      'subcontractor': 'Subcontractor',
      'user': 'User'
    };
    return labels[role] || role;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionsDisplay = (user) => {
    if (user.role === 'super_admin') {
      return <span className="text-xs font-semibold text-red-600">All Permissions</span>;
    } else if (user.role === 'user') {
      return <span className="text-xs text-gray-400">Read-Only</span>;
    } else if (user.permissions && user.permissions.length > 0) {
      return (
        <div className="flex flex-wrap gap-1">
          {user.permissions.slice(0, 3).map(p => (
            <span key={p} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
              {p.replace(/_/g, ' ')}
            </span>
          ))}
          {user.permissions.length > 3 && (
            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
              +{user.permissions.length - 3}
            </span>
          )}
        </div>
      );
    } else {
      return <span className="text-xs text-gray-400">No permissions</span>;
    }
  };

  const canEdit = isSuperAdmin;

  if (loading) {
    return (
      <AdminLayout activeTab="users" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-500">Loading users...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="users" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">👥 Users</h1>
          <p className="text-gray-500 text-sm">Manage users and their permissions</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
          >
            <FaUserPlus size={14} /> Add User
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-xl p-2 border border-gray-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'users' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaUsers className="inline mr-2" /> Users
        </button>
        <button
          onClick={() => { setActiveTab('inspectors'); fetchInspectors(); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'inspectors' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaUserCheck className="inline mr-2" /> Site Inspectors
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
      {/* Search */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm font-medium text-navy">{u.full_name}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{u.email}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{u.phone || '—'}</td>
                    <td className="px-6 py-3">
                      <span className={getRoleBadge(u.role)}>{getRoleLabel(u.role)}</span>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${u.is_active !== false ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {u.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">
                      {formatDate(u.last_login)}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex gap-2">
                        {canEdit && (
                          <button
                            onClick={() => openEditModal(u)}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Edit"
                          >
                            <FaEdit size={16} />
                          </button>
                        )}
                        {/* Support tool: open the portal as this person (super admin only — renders
                            nothing for anyone else, and nothing while already viewing as someone). */}
                        <ViewAsButton userId={u.id} name={u.full_name || u.email} />
                        {canEdit && u.id !== user.id && u.email !== 'arthur@onepurposelogistics.com' && (
                          <button
                            onClick={() => handleDeleteClick(u)}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <FaTrash size={16} />
                          </button>
                        )}
                        {!canEdit && (
                          <span className="text-xs text-gray-400">Read-only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* Site Inspectors Tab */}
      {activeTab === 'inspectors' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
            <div>
              <h3 className="font-semibold text-navy flex items-center gap-2">
                <FaUserCheck className="text-green" /> Site Inspectors
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Subcontracted surveyors / rigging inspectors used for "Site Inspector Required" inspections.
              </p>
            </div>
            <button
              onClick={openAddInspector}
              className="px-3 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors flex items-center gap-2"
            >
              <FaUserPlus size={12} /> Add Inspector
            </button>
          </div>

          {inspectorsLoading ? (
            <p className="text-sm text-gray-500 py-4">Loading inspectors...</p>
          ) : inspectors.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <FaUserCheck className="text-3xl text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No site inspectors yet. Add your first one above.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Telephone</th>
                    <th className="py-2 pr-4">Coverage Area</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inspectors.map((inspector) => (
                    <tr key={inspector.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-2.5 pr-4">
                        <p className="font-medium text-navy">{inspector.name}</p>
                        {inspector.email && <p className="text-xs text-gray-400">{inspector.email}</p>}
                      </td>
                      <td className="py-2.5 pr-4">{inspector.telephone || '-'}</td>
                      <td className="py-2.5 pr-4">{inspector.coverage_area || '-'}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${inspector.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {inspector.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditInspector(inspector)}
                          className="text-navy hover:text-green transition-colors p-1"
                          title="Edit"
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          onClick={() => handleToggleInspectorActive(inspector)}
                          className="text-gray-500 hover:text-green transition-colors p-1"
                          title={inspector.active ? 'Deactivate' : 'Activate'}
                        >
                          <FaCheck size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteInspector(inspector)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add/Edit Inspector Form */}
          {showInspectorForm && (
            <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-navy mb-3">
                {editingInspector ? 'Edit Inspector' : 'Add New Inspector'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Full Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Johan van der Merwe"
                    value={inspectorForm.name}
                    onChange={(e) => handleInspectorFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Telephone</label>
                  <input
                    type="text"
                    placeholder="e.g. 072 123 4567"
                    value={inspectorForm.telephone}
                    onChange={(e) => handleInspectorFormChange('telephone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="e.g. johan@example.com"
                    value={inspectorForm.email}
                    onChange={(e) => handleInspectorFormChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Coverage Area</label>
                  <input
                    type="text"
                    placeholder="e.g. Gauteng, Johannesburg, Mpumalanga"
                    value={inspectorForm.coverage_area}
                    onChange={(e) => handleInspectorFormChange('coverage_area', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                {/* 🔧 Fixed: inline brand green so it can never be white-on-white */}
                <button
                  onClick={handleSaveInspector}
                  className="px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  <FaCheck size={14} />
                  {editingInspector ? 'Save Changes' : 'Add Inspector'}
                </button>
                <button
                  onClick={() => setShowInspectorForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAddModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">Add New User</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleAddUser} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.role === 'super_admin' && 'Grants ALL permissions automatically'}
                    {formData.role === 'user' && 'Read-only access'}
                    {formData.role === 'admin' && 'Select permissions below'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>
                {formData.role === 'admin' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {allPermissions.map(p => (
                        <label key={p.key} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(p.key)}
                            onChange={() => togglePermission(p.key)}
                            className="w-4 h-4 text-green-600 focus:ring-green-500"
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Select specific permissions for this admin</p>
                  </div>
                )}
                {formData.role === 'super_admin' && (
                  <div className="md:col-span-2">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 flex items-center gap-2">
                        <FaCheck className="text-green-600" /> Super Admin has ALL permissions automatically
                      </p>
                    </div>
                  </div>
                )}
                {formData.role === 'user' && (
                  <div className="md:col-span-2">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 flex items-center gap-2">
                        <FaShieldAlt className="text-gray-400" /> User role has read-only access
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? <FaSpinner className="animate-spin" /> : <FaUserPlus />}
                  {submitting ? 'Creating...' : 'Create User & Send Invite'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">
                An invite email will be sent to the user with a magic link to set their password
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrash className="text-3xl text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-navy">Delete User</h2>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to delete <strong className="text-navy">{deleteTarget.full_name}</strong>?
              </p>
              <p className="text-xs text-red-500 mt-1">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >Cancel</button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <><FaSpinner className="animate-spin" /> Deleting...</> : <><FaTrash size={14} /> Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Site Inspector Modal (custom — replaces window.confirm) */}
      {showInspectorDeleteModal && inspectorToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!deletingInspector) setShowInspectorDeleteModal(false); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrash className="text-3xl text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-navy">Delete Site Inspector</h2>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to delete <strong className="text-navy">{inspectorToDelete.name}</strong>?
              </p>
              <p className="text-xs text-red-500 mt-1">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowInspectorDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >Cancel</button>
              <button
                onClick={confirmDeleteInspector}
                disabled={deletingInspector}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingInspector ? <><FaSpinner className="animate-spin" /> Deleting...</> : <><FaTrash size={14} /> Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEditModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">Edit User</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleEditUser} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={selectedUser.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>
                {formData.role === 'admin' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {allPermissions.map(p => (
                        <label key={p.key} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(p.key)}
                            onChange={() => togglePermission(p.key)}
                            className="w-4 h-4 text-green-600 focus:ring-green-500"
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {formData.role === 'super_admin' && (
                  <div className="md:col-span-2">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 flex items-center gap-2">
                        <FaCheck className="text-green-600" /> Super Admin has ALL permissions automatically
                      </p>
                    </div>
                  </div>
                )}
                {formData.role === 'user' && (
                  <div className="md:col-span-2">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 flex items-center gap-2">
                        <FaShieldAlt className="text-gray-400" /> User role has read-only access
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? <FaSpinner className="animate-spin" /> : <FaEdit />}
                  {submitting ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <InviteLinkModal
        open={!!inviteInfo}
        onClose={() => setInviteInfo(null)}
        company={inviteInfo?.company}
        email={inviteInfo?.email}
        link={inviteInfo?.link}
        emailSent={inviteInfo?.emailSent}
      />
    </AdminLayout>
  );
}

export default UsersPage;